import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@/components/Icon";
import { OddsButton } from "./sb";
import { resolvePlayerHeadshot } from "@/lib/sportsAssets";
import { buildCategorySections } from "@/lib/propCatalog";

/* MarketProps — the FanDuel-style, category-contained player-props surface. A category is a flat,
   ordered list of collapsible SIBLING SECTIONS:

        Receiving Yards O/U   →   Alt Receiving Yards   →   Receptions O/U   →   Alt Receptions   → …

   Related markets stay together: each market's STANDARD section renders immediately before its ALT
   sibling. There is NO Main/Alt toggle, NO global threshold rail that filters the player list, and NO
   "N more players in Alt…" affordance.

     • STANDARD section → one compact O/U row per player: line + Over/Under (or Yes/No for scorer
                          markets). A normal sportsbook table.
     • ALT section      → one row per player showing ALL of that player's real alternate thresholds
                          inline (25+ / 40+ / 50+ …), numerically sorted, in a horizontally scrollable
                          odds rail. The player never disappears behind a chosen threshold.

   Only real provider outcomes render — nothing is fabricated/interpolated. Every price passes the
   EXACT catalog outcome object straight to onPick, so bet placement / suspension / freshness /
   odds-change handling are identical to the board. One responsive tree (no desktop/mobile fork).
   Open/closed state is keyed by stable section keys (`propType:standard` / `propType:alt`) so it
   survives the event page's 3s market refresh and siblings collapse independently. */

const cardStyle = { background: "#111114", boxShadow: "inset 0 0 0 1px #1F1F23, inset 0 1px 0 rgba(255,255,255,.05), 0 10px 26px rgba(0,0,0,.22)" };

const ini = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  return w.length ? (w.length === 1 ? w[0].slice(0, 2) : w[0][0] + w[w.length - 1][0]).toUpperCase() : "—";
};

function Headshot({ player, image, size = 34 }) {
  const src = resolvePlayerHeadshot({ player, providerImageUrl: image });
  const [broken, setBroken] = useState(false);
  const showImg = src && !broken;
  return (
    <span className="relative flex shrink-0 items-center justify-center overflow-hidden rounded-full"
      style={{ width: size, height: size, background: "radial-gradient(circle at 50% 30%, #26262B 0%, #18181C 70%)", boxShadow: "inset 0 0 0 1px #2F2F35" }}>
      {showImg
        ? <img src={src} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" onError={() => setBroken(true)} />
        : <span style={{ font: "800 12px Manrope", color: "#D4D4D8" }}>{ini(player)}</span>}
    </span>
  );
}

function PlayerCell({ player, image }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Headshot player={player} image={image} />
      <span className="min-w-0 truncate" style={{ font: "700 14px Outfit", color: "#fff" }}>{player}</span>
    </div>
  );
}

// STANDARD row: player + line + Over/Under (or Yes/No). Compact, aligned, one responsive tree.
function StandardRow({ row, onPick, picked }) {
  const s = row.standard;
  const yesno = !!s.yesno;
  const over = yesno ? s.yes : s.over;
  const under = yesno ? s.no : s.under;
  const cols = yesno
    ? "grid-cols-[minmax(0,1fr)_64px_64px] sm:grid-cols-[minmax(0,1fr)_88px_88px]"
    : "grid-cols-[minmax(0,1fr)_38px_64px_64px] sm:grid-cols-[minmax(0,1fr)_56px_88px_88px]";
  const cell = (o, label) => (o
    ? <OddsButton variant="rowM" line={label} price={o.odds} selected={picked(o)}
        locked={o.suspended === true || o.active === false} onClick={() => onPick(o)} testid={`odds-${o.id}`} />
    : <OddsButton na variant="rowM" />);
  return (
    <div className={`grid items-center gap-2 px-3 py-2 sm:px-4 ${cols}`}
      style={{ borderTop: "1px solid #17171A" }} data-testid={`prop-row-${row.player}`}>
      <PlayerCell player={row.player} image={row.image} />
      {!yesno && (
        <span className="text-center tabular-nums" style={{ font: "800 16px Manrope", color: "#fff" }}>{s.line ?? "—"}</span>
      )}
      {cell(over, yesno ? "Yes" : "Over")}
      {cell(under, yesno ? "No" : "Under")}
    </div>
  );
}

// ALT row: player identity + a horizontally scrollable rail of ALL their real alternate thresholds
// (25+ / 40+ / 50+ …), each carrying its exact provider outcome. No threshold filter, no wrapping
// button soup — the rail scrolls locally (page never scrolls sideways).
function AltRow({ row, onPick, picked }) {
  return (
    <div className="px-3 py-2.5 sm:px-4" style={{ borderTop: "1px solid #17171A" }} data-testid={`prop-row-${row.player}`}>
      <div className="mb-2"><PlayerCell player={row.player} image={row.image} /></div>
      <div className="no-scrollbar flex gap-2 overflow-x-auto pb-0.5"
        style={{ overscrollBehaviorX: "contain", WebkitOverflowScrolling: "touch" }}
        data-testid={`prop-alt-rail-${row.player}`}>
        {row.alts.map((a) => {
          const o = a.outcome;
          return (
            <div key={o.id} className="shrink-0" style={{ width: 74 }}>
              <OddsButton variant="stack" line={a.label} price={o.odds} selected={picked(o)}
                locked={o.suspended === true || o.active === false}
                onClick={() => onPick(o)} testid={`odds-${o.id}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// One COLLAPSIBLE sibling section — a standard O/U table OR an alt ladder for a single market.
export function PropSection({ section, isOpen, onToggle, onPick, picked }) {
  const isAlt = section.type === "alt";
  return (
    <div className="overflow-hidden rounded-[18px]" style={cardStyle} data-testid={`prop-section-${section.key}`}>
      <button type="button" onClick={onToggle} aria-expanded={isOpen} data-testid={`prop-section-toggle-${section.key}`}
        className="flex w-full items-center justify-between px-4 text-left transition-colors hover:bg-white/[0.02] sm:px-5"
        style={{ height: 52, borderBottom: isOpen ? "1px solid #1A1A1E" : "none" }}>
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="truncate font-display text-[15px] font-extrabold tracking-tight text-white">{section.label}</span>
          <span className="shrink-0 text-xs text-[#71717a]">{section.playerCount} player{section.playerCount === 1 ? "" : "s"}</span>
        </span>
        <Icon name="chevron-down" size={16} className={`shrink-0 text-[#71717a] transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="pb-1">
          {isAlt
            ? section.players.map((row) => (
                <AltRow key={row.player} row={row} onPick={onPick} picked={picked} />
              ))
            : section.players.map((row) => (
                <StandardRow key={row.player} row={row} onPick={onPick} picked={picked} />
              ))}
        </div>
      )}
    </div>
  );
}

/* MarketProps — a category's markets flattened into ordered sibling sections (standard then alt, per
   market). Default-opens the FIRST section of a category once; all others start collapsed. Open/closed
   state is held here keyed by the stable section key so it survives the 3s event refresh and siblings
   collapse independently. */
export default function MarketProps({ markets = [], onPick, picked }) {
  const sections = buildCategorySections(markets);
  const keyer = sections.map((s) => s.key).join("|");
  const [open, setOpen] = useState({});
  const seeded = useRef(new Set());

  useEffect(() => {
    // Seed the sportsbook default (first section open) ONCE per category signature, and only if the
    // user hasn't already touched that section. The event page refreshes markets every 3s, so we must
    // never re-open a section the user collapsed — the `firstKey in o` guard protects that. Stale keys
    // for sections not currently rendered stay inert.
    if (!sections.length) return;
    if (seeded.current.has(keyer)) return;
    seeded.current.add(keyer);
    const firstKey = sections[0].key;
    setOpen((o) => (firstKey in o ? o : { ...o, [firstKey]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyer]);

  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  if (!sections.length) return null;
  return (
    <div className="space-y-3">
      {sections.map((s) => (
        <PropSection key={s.key} section={s} isOpen={!!open[s.key]} onToggle={() => toggle(s.key)}
          onPick={onPick} picked={picked} />
      ))}
    </div>
  );
}
